/**
 * Transport seam for JSON-bodied client → API requests. Accepts ONLY
 * `Built<T>` bodies — values produced by `seal()` after a safeParse in a
 * canonical builder. A hand-rolled literal in a component does not satisfy
 * `Built<T>` and won't compile here, so the seam structurally prevents
 * "skip the schema, post anyway" regressions.
 *
 * Scope: first increment is chat (`lib/chat/chat-upsert.ts` +
 * `lib/chat/chat-send.ts`). Other clusters (checkout, offers, auctions,
 * orders, cart) retrofit incrementally — see
 * `.tondocs/RELIABILITY_ROADMAP.md §4 #3c`.
 *
 * Why this is a separate file (not part of the per-area builders):
 *  - All POST-JSON builders share the same shape (URL + headers + JSON
 *    body). Centralising prevents drift between, e.g.,
 *    `content-type: application/json` and `Content-Type: application/json`
 *    or `credentials: 'include'` vs `'same-origin'`.
 *  - Inverting the dependency: builders depend on `postJson`, not on
 *    `fetch`. ESLint can then forbid raw `fetch('/api/...')` in builders
 *    and rely on `postJson` being the single point of egress.
 *
 * @see packages/contract/src/built.ts — `Built<T>` brand + `seal()`
 * @see lib/chat/chat-send.ts / lib/chat/chat-upsert.ts — first callers
 * @see .tondocs/RELIABILITY_ROADMAP.md §4 #3c
 */

import type { Built } from "@kraahl/contract"

export interface PostJsonInit {
  method?: "POST" | "PUT" | "PATCH"
  headers?: Record<string, string>
  credentials?: RequestCredentials
  signal?: AbortSignal
}

/**
 * POST a `Built<TBody>` to `url`. The seam is wire-only: it does not
 * read/parse the response — that's the builder's job (typed result on
 * 2xx, typed error on non-2xx).
 */
export async function postJson<TBody>(
  url: string,
  body: Built<TBody>,
  init?: PostJsonInit,
): Promise<Response> {
  return fetch(url, {
    method: init?.method ?? "POST",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    credentials: init?.credentials,
    signal: init?.signal,
    body: JSON.stringify(body),
  })
}
